import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ClipboardList, Users, ChevronRight, Sparkles, Columns3, Upload, User, Pencil, CalendarClock } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import type { Application, ApplicationStatus } from '@core/types';
import { applicationService } from '@core/supabase/services/applicationService';
import {
  interviewEventService,
  type InterviewEventWithApplication,
} from '@core/supabase/services/interviewEventService';
import { ALL_STATUSES, PIPELINE_STATUS_LABEL } from '@core/constants/pipelineStages';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { SyncStatusBadge } from '@shared/components/SyncStatusBadge';
import { useToast } from '@shared/hooks/useToast';
import { formatCreatedWhen } from '@shared/lib/formatCreatedWhen';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { EmptyState } from '@shared/components/ui/EmptyState';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { TablePagination } from '@shared/components/ui/TablePagination';
import { DataTableToolbar } from '@shared/components/ui/DataTableToolbar';
import { ApplicationStatusChip } from '@shared/components/ui/StatusChip';
import { ListOperationsDropdown } from '@shared/components/ui/ListOperationsDropdown';
import { BookingFlow } from './components/BookingFlow';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { ConfigureListColumnsModal, colClass } from '@shared/components/ui/ConfigureListColumnsModal';
import { SortableTh } from '@shared/components/ui/SortableTableHeader';
import { GenericManageRightsModal } from '@shared/components/list/GenericManageRightsModal';
import { useListColumnVisibility } from '@shared/hooks/useListColumnVisibility';
import type { ListColumnCatalogItem } from '@shared/lib/listColumnPreferences';
import { listExportNotImplemented } from '@shared/lib/listExportStub';
import { InterviewWizardModal } from './InterviewWizardModal';
import { ContextAiAssistModal } from '@shared/components/ai/ContextAiAssistModal';

type CandidateInterviewGroup = {
  candidateId: string;
  applications: Application[];
};

function formatInterviewWhen(iso: string | null): string {
  return formatDateTimeGmt(iso);
}

function isInterviewPipelineFocus(status: ApplicationStatus): boolean {
  return (
    status === 'shortlisted' ||
    status.startsWith('interview_') ||
    status === 'no_show' ||
    status.startsWith('offer_') ||
    status === 'placed'
  );
}

function statusRank(s: ApplicationStatus): number {
  const i = ALL_STATUSES.indexOf(s);
  return i >= 0 ? i : 0;
}

function phaseBreakdown(apps: Application[]): string {
  const counts = new Map<string, number>();
  for (const a of apps) {
    const lab = PIPELINE_STATUS_LABEL[a.status];
    counts.set(lab, (counts.get(lab) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([l, n]) => (n > 1 ? `${l}×${n}` : l))
    .join(' · ');
}

/** One comma-separated string per column (distinct order preserved). */
function uniqueJobTitlesJoined(apps: Application[]): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const a of apps) {
    const t = a.job?.title?.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out.length ? out.join(', ') : '—';
}

function uniqueClientNamesJoined(apps: Application[]): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const a of apps) {
    const t = a.client?.name?.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out.length ? out.join(', ') : '—';
}

function nextInterviewForGroup(events: InterviewEventWithApplication[], appIds: Set<string>): string {
  const relevant = events
    .filter((e) => appIds.has(e.application_id) && e.scheduled_at)
    .sort((a, b) => new Date(a.scheduled_at!).getTime() - new Date(b.scheduled_at!).getTime());
  if (relevant.length === 0) return '—';
  const now = Date.now();
  const upcoming = relevant.find((e) => new Date(e.scheduled_at!).getTime() >= now);
  const pick = upcoming ?? relevant[relevant.length - 1]!;
  return formatInterviewWhen(pick.scheduled_at);
}

const INTERVIEWS_HUB_COLUMNS: ListColumnCatalogItem[] = [
  { id: 'name', label: 'Name' },
  { id: 'email', label: 'Email', optional: true },
  { id: 'job_titles', label: 'Job titles' },
  { id: 'clients', label: 'Clients' },
  { id: 'location', label: 'Location', optional: true },
  { id: 'phase', label: 'Interview phase' },
  { id: 'furthest', label: 'Furthest stage', optional: true },
  { id: 'next', label: 'Next interview', optional: true },
  { id: 'created', label: 'Created', optional: true },
];

function groupByCandidate(apps: Application[]): CandidateInterviewGroup[] {
  const map = new Map<string, Application[]>();
  for (const a of apps) {
    const key = a.candidate_id;
    const arr = map.get(key) ?? [];
    arr.push(a);
    map.set(key, arr);
  }
  const rows: CandidateInterviewGroup[] = [];
  for (const [candidateId, list] of map) {
    list.sort((x, y) => new Date(y.submitted_at).getTime() - new Date(x.submitted_at).getTime());
    rows.push({ candidateId, applications: list });
  }
  rows.sort((a, b) => {
    const na = a.applications[0]?.candidate?.full_name ?? '';
    const nb = b.applications[0]?.candidate?.full_name ?? '';
    return na.localeCompare(nb, undefined, { sensitivity: 'base' });
  });
  return rows;
}

export function InterviewsHubPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<Application[]>([]);
  const [interviewRows, setInterviewRows] = useState<InterviewEventWithApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<'all' | 'interview_focus'>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckRef = useRef<HTMLInputElement>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [bookingFlowOpen, setBookingFlowOpen] = useState(false);
  const { toast, showToast } = useToast();
  const [scheduleAiOpen, setScheduleAiOpen] = useState(false);
  const [configureOpen, setConfigureOpen] = useState(false);
  const [manageRightsOpen, setManageRightsOpen] = useState(false);

  const hubCatalog = useMemo(() => INTERVIEWS_HUB_COLUMNS, []);
  const { show, setColumn, reset, visible } = useListColumnVisibility(
    'interviews_hub',
    user?.orgId ?? null,
    hubCatalog,
  );

  const reload = useCallback(async () => {
    if (!user?.orgId) return;
    const [apps, events] = await Promise.all([
      applicationService.list(user.orgId, { limit: 2000 }),
      interviewEventService.listForOrgWithApplications(user.orgId, { limit: 2000 }),
    ]);
    setApplications(Array.isArray(apps) ? apps : []);
    setInterviewRows(Array.isArray(events) ? events : []);
  }, [user?.orgId]);

  useEffect(() => {
    if (!user?.orgId) return;
    setLoading(true);
    reload()
      .catch(() => {
        setApplications([]);
        setInterviewRows([]);
      })
      .finally(() => setLoading(false));
  }, [user?.orgId, reload]);

  useEffect(() => {
    setPage(1);
  }, [search, scope]);

  const filteredApps = useMemo(() => {
    const q = search.trim().toLowerCase();
    return applications.filter((a) => {
      if (scope === 'interview_focus' && !isInterviewPipelineFocus(a.status)) return false;
      if (!q) return true;
      const cand = a.candidate;
      const job = a.job;
      const client = a.client;
      const hay = [
        cand?.full_name,
        cand?.email,
        job?.title,
        client?.name,
        cand?.current_company,
        cand?.current_title,
        cand?.location,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [applications, search, scope]);

  type HubSortCol = 'name' | 'email' | 'created';
  const [hubSortBy, setHubSortBy] = useState<HubSortCol>('created');
  const [hubSortAsc, setHubSortAsc] = useState(false);

  const groupedRows = useMemo(() => {
    const rows = groupByCandidate(filteredApps);
    const dir = hubSortAsc ? 1 : -1;
    const strCmp = (a: CandidateInterviewGroup, b: CandidateInterviewGroup, get: (g: CandidateInterviewGroup) => string) =>
      dir * get(a).localeCompare(get(b), undefined, { sensitivity: 'base' });
    const dateCmp = (a: CandidateInterviewGroup, b: CandidateInterviewGroup) => {
      const ca = a.applications[0]?.candidate?.created_at ?? '';
      const cb = b.applications[0]?.candidate?.created_at ?? '';
      return dir * (new Date(ca).getTime() - new Date(cb).getTime());
    };
    rows.sort((a, b) => {
      switch (hubSortBy) {
        case 'email':
          return strCmp(a, b, (g) => g.applications[0]?.candidate?.email ?? '');
        case 'created':
          return dateCmp(a, b);
        case 'name':
        default:
          return strCmp(a, b, (g) => g.applications[0]?.candidate?.full_name ?? '');
      }
    });
    return rows;
  }, [filteredApps, hubSortBy, hubSortAsc]);

  const handleHubSort = (col: HubSortCol) => {
    if (hubSortBy === col) {
      setHubSortAsc((x) => !x);
    } else {
      setHubSortBy(col);
      setHubSortAsc(col === 'created' ? false : true);
    }
    setPage(1);
  };

  const pageRows = useMemo(
    () => groupedRows.slice((page - 1) * pageSize, page * pageSize),
    [groupedRows, page, pageSize]
  );

  const interviewScheduleContext = useMemo(
    () =>
      [
        `Candidates (grouped): ${groupedRows.length}`,
        `Applications (filtered): ${filteredApps.length}`,
        `Interview events: ${interviewRows.length}`,
      ].join('\n'),
    [groupedRows.length, filteredApps.length, interviewRows.length],
  );

  useEffect(() => {
    if (headerCheckRef.current) {
      const n = pageRows.length;
      const selOnPage = pageRows.filter((r) => selectedIds.has(r.candidateId)).length;
      headerCheckRef.current.indeterminate = n > 0 && selOnPage > 0 && selOnPage < n;
    }
  }, [pageRows, selectedIds]);

  const openWizard = () => setWizardOpen(true);
  const closeWizard = () => setWizardOpen(false);

  const exportInterviewsHubCsv = useCallback(
    (scope: 'toolbar' | 'selection' | 'all') => {
      let candidateSet: Set<string>;
      if (scope === 'toolbar') {
        candidateSet =
          selectedIds.size > 0 ? selectedIds : new Set(groupedRows.map((g) => g.candidateId));
      } else if (scope === 'selection') {
        if (selectedIds.size === 0) {
          showToast({ title: 'Select rows to export, or use CSV — all under Operations.', kind: 'error' });
          return;
        }
        candidateSet = selectedIds;
      } else {
        candidateSet = new Set(groupedRows.map((g) => g.candidateId));
      }
      const rows = filteredApps.filter((a) => candidateSet.has(a.candidate_id));
      if (rows.length === 0) {
        showToast({ title: 'Nothing to export for the current filters.', kind: 'error' });
        return;
      }
      const headers = ['Candidate', 'Email', 'Job', 'Client', 'Interview phase', 'Next interview', 'Status'];
      const csvRows = [
        headers.join(','),
        ...rows.map((a) =>
          [
            `"${(a.candidate?.full_name ?? '').replace(/"/g, '""')}"`,
            `"${(a.candidate?.email ?? '').replace(/"/g, '""')}"`,
            `"${(a.job?.title ?? '').replace(/"/g, '""')}"`,
            `"${(a.client?.name ?? '').replace(/"/g, '""')}"`,
            `"${PIPELINE_STATUS_LABEL[a.status].replace(/"/g, '""')}"`,
            `"${nextInterviewForGroup(interviewRows, new Set([a.id])).replace(/"/g, '""')}"`,
            `"${a.status.replace(/"/g, '""')}"`,
          ].join(','),
        ),
      ];
      const blob = new Blob(['\ufeff' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const el = document.createElement('a');
      el.href = url;
      el.download = `interviews_applications_${new Date().toISOString().slice(0, 10)}.csv`;
      el.click();
      URL.revokeObjectURL(url);
    },
    [selectedIds, groupedRows, filteredApps, interviewRows],
  );

  if (!user?.orgId) return <FullPageSpinner />;
  if (loading) return <FullPageSpinner />;

  const allOnPageSelected = pageRows.length > 0 && pageRows.every((r) => selectedIds.has(r.candidateId));

  return (
    <div>
      {toast}
      <SyncStatusBadge className="mb-3" />
      <PageHeader
        variant="minimal"
        breadcrumb={[{ label: 'Data', href: '/interviews' }, { label: 'Interviews' }]}
        breadcrumbRowEnd={
          <>
            <button
              type="button"
              onClick={() => setBookingFlowOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-[8px] border px-3 py-1.5 text-[13px] font-medium vscode-border vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
              title="Configure booking link & availability"
            >
              <CalendarClock className="h-4 w-4" aria-hidden />
              Booking info
            </button>
            <ListOperationsDropdown
              objectHistoryHref={objectHistoryUrl('interviews_hub')}
              onExport={(fmt, scope) => {
                if (fmt === 'csv') exportInterviewsHubCsv(scope);
                else listExportNotImplemented('Interviews hub', fmt, scope);
              }}
              extraActions={[
                {
                  id: 'ai-schedule',
                  label: 'AI scheduling assist',
                  icon: <Sparkles className="vscode-text-secondary" strokeWidth={2} />,
                  onClick: () => setScheduleAiOpen(true),
                },
                {
                  id: 'prep',
                  label: 'Prep & scorecards',
                  icon: <ClipboardList className="vscode-text-secondary" strokeWidth={2} />,
                  onClick: () => navigate('/interviews/prep'),
                },
                {
                  id: 'configure',
                  label: 'Configure',
                  icon: <Columns3 className="vscode-text-secondary" strokeWidth={2} />,
                  onClick: () => setConfigureOpen(true),
                },
                {
                  id: 'import',
                  label: 'Import',
                  icon: <Upload className="vscode-text-secondary" strokeWidth={2} />,
                  onClick: () =>
                    showToast({ title: 'Interview rows come from applications. Use New interview or open the pipeline to add candidates.', kind: 'error' }),
                },
                {
                  id: 'rights',
                  label: 'Manage Rights',
                  icon: <User className="vscode-text-secondary" strokeWidth={2} />,
                  onClick: () => setManageRightsOpen(true),
                },
              ]}
            />
          </>
        }
      />

      {/* Booking flow modal */}
      <BookingFlow
        open={bookingFlowOpen}
        onClose={() => setBookingFlowOpen(false)}
      />

      <div className="relative mb-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 vscode-text-tertiary" />
          <input
            className="input pl-9"
            placeholder="Search by candidate, email, job, or client…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-xs vscode-text-tertiary shrink-0">
          <span>Show</span>
          <select className="input text-sm py-1.5 max-w-[200px]" value={scope} onChange={(e) => setScope(e.target.value as 'all' | 'interview_focus')}>
            <option value="all">All applications</option>
            <option value="interview_focus">Interview &amp; offer stages</option>
          </select>
        </label>
      </div>

      {applications.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No applications yet"
          description="Applications appear when candidates apply to jobs. Open Applications or the pipeline to add candidates."
          action={
            <Link to="/applications" className="btn-primary">
              Go to applications
            </Link>
          }
        />
      ) : groupedRows.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No matching rows"
          description="Try clearing search or switching to “All applications”."
        />
      ) : (
        <div className="card overflow-hidden p-0">
          <DataTableToolbar hasTable newLabel="New interview" newOnClick={openWizard} />
          {selectedIds.size > 0 && (
            <div className="ggj-data-bulk-bar flex flex-wrap items-center gap-2 px-4 py-2">
              <span className="text-sm font-medium vscode-text-primary">{selectedIds.size} selected</span>
              <button
                type="button"
                className="btn-secondary inline-flex items-center gap-1 text-xs"
                disabled={selectedIds.size !== 1}
                onClick={() => {
                  const id = Array.from(selectedIds)[0];
                  if (id) navigate(`/interviews/candidate/${id}`);
                }}
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
            </div>
          )}
          <div className="overflow-x-auto min-w-0">
          <table className="ggj-data-table min-w-[1100px]">
            <thead>
              <tr className="text-left">
                <th className="w-10">
                  <input
                    type="checkbox"
                    ref={headerCheckRef}
                    checked={allOnPageSelected}
                    onChange={() => {
                      if (allOnPageSelected) {
                        const next = new Set(selectedIds);
                        pageRows.forEach((r) => next.delete(r.candidateId));
                        setSelectedIds(next);
                      } else {
                        const next = new Set(selectedIds);
                        pageRows.forEach((r) => next.add(r.candidateId));
                        setSelectedIds(next);
                      }
                    }}
                    className="rounded border-[var(--vscode-border-input)]"
                  />
                </th>
                <SortableTh
                  label="Name"
                  column="name"
                  current={hubSortBy}
                  asc={hubSortAsc}
                  onSort={handleHubSort}
                  className={colClass(show('name'), '')}
                />
                <SortableTh
                  label="Email"
                  column="email"
                  current={hubSortBy}
                  asc={hubSortAsc}
                  onSort={handleHubSort}
                  className={colClass(show('email'), '')}
                />
                <th className={colClass(show('job_titles'), 'px-4 py-3')}>Job titles</th>
                <th className={colClass(show('clients'), 'px-4 py-3')}>Clients</th>
                <th className={colClass(show('location'), 'px-4 py-3')}>Location</th>
                <th className={colClass(show('phase'), 'px-4 py-3')}>Interview phase</th>
                <th className={colClass(show('furthest'), 'px-4 py-3')}>Furthest stage</th>
                <th className={colClass(show('next'), 'px-4 py-3')}>Next interview</th>
                <SortableTh
                  label="Created"
                  column="created"
                  current={hubSortBy}
                  asc={hubSortAsc}
                  onSort={handleHubSort}
                  className={colClass(show('created'), 'whitespace-nowrap')}
                />
                <th className="text-right w-28"></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row) => {
                const first = row.applications[0]!;
                const furthestApp = row.applications.reduce(
                  (acc, a) => (statusRank(a.status) > statusRank(acc.status) ? a : acc),
                  first
                );
                const cand = first.candidate;
                const appIdSet = new Set(row.applications.map((a) => a.id));
                const href = `/interviews/candidate/${row.candidateId}`;
                return (
                  <tr
                    key={row.candidateId}
                    className={`transition-colors cursor-pointer ${selectedIds.has(row.candidateId) ? 'bg-brand-500/10' : ''}`}
                    onClick={(e) => {
                      const t = e.target as HTMLElement;
                      if (t.closest('input[type="checkbox"]') || t.closest('a')) return;
                      navigate(href);
                    }}
                  >
                    <td className="w-10" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(row.candidateId)}
                        onChange={() => {
                          const next = new Set(selectedIds);
                          if (next.has(row.candidateId)) next.delete(row.candidateId);
                          else next.add(row.candidateId);
                          setSelectedIds(next);
                        }}
                        className="rounded border-[var(--vscode-border-input)]"
                      />
                    </td>
                    <td className={colClass(show('name'), 'px-4 py-3')}>
                      <Link
                        to={href}
                        className="font-medium vscode-text-primary hover:text-brand-400 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {cand?.full_name ?? '—'}
                      </Link>
                    </td>
                    <td className={colClass(show('email'), 'px-4 py-3 text-sm vscode-text-secondary')}>
                      {cand?.email?.trim() ? cand.email : '—'}
                    </td>
                    <td className={colClass(show('job_titles'), 'px-4 py-3 text-sm vscode-text-primary max-w-md')}>
                      {uniqueJobTitlesJoined(row.applications)}
                    </td>
                    <td className={colClass(show('clients'), 'px-4 py-3 text-sm vscode-text-secondary max-w-md')}>
                      {uniqueClientNamesJoined(row.applications)}
                    </td>
                    <td className={colClass(show('location'), 'px-4 py-3 vscode-text-secondary')}>{cand?.location ?? '—'}</td>
                    <td className={colClass(show('phase'), 'px-4 py-3 text-sm vscode-text-primary leading-snug')}>
                      {phaseBreakdown(row.applications)}
                    </td>
                    <td className={colClass(show('furthest'), 'px-4 py-3 text-sm')}>
                      <ApplicationStatusChip status={furthestApp.status} />
                    </td>
                    <td className={colClass(show('next'), 'px-4 py-3 text-sm vscode-text-secondary whitespace-nowrap')}>
                      {nextInterviewForGroup(interviewRows, appIdSet)}
                    </td>
                    <td className={colClass(show('created'), 'px-4 py-3 text-sm vscode-text-secondary tabular-nums whitespace-nowrap')}>
                      {formatCreatedWhen(cand?.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <Link
                        to={href}
                        className="ggj-table-btn"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View
                        <ChevronRight className="w-3.5 h-3.5" aria-hidden />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <TablePagination
            page={page}
            pageSize={pageSize}
            totalItems={groupedRows.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </div>
      )}

      <InterviewWizardModal
        open={wizardOpen}
        onClose={closeWizard}
        orgId={user.orgId}
        orgName={user.orgName}
        recruiterName={user.name}
        applications={applications}
        scheduleDayKey={null}
        initialApplicationId={null}
        onCreated={() => {
          void reload();
        }}
      />

      <ContextAiAssistModal
        open={scheduleAiOpen}
        onClose={() => setScheduleAiOpen(false)}
        orgId={user?.orgId}
        feature="ai_assist_interview_scheduling"
        preset="interview_scheduling_assistant"
        title="AI — scheduling assistant"
        contextHint={interviewScheduleContext}
        original=""
        showApplyActions={false}
        onApply={() => {}}
      />
      <ConfigureListColumnsModal
        open={configureOpen}
        onClose={() => setConfigureOpen(false)}
        entityLabel="Interviews"
        columns={INTERVIEWS_HUB_COLUMNS}
        value={visible}
        onChange={setColumn}
        onReset={reset}
      />
      <GenericManageRightsModal
        open={manageRightsOpen}
        onClose={() => setManageRightsOpen(false)}
        orgId={user?.orgId ?? null}
        entityKey="interviews_hub"
        subtitle="Per-user access for the Interviews hub list — stored locally per browser."
      />
    </div>
  );
}
